/* Diamonds Gallery — custom pair/single ring selection for Shopify theme */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-dg-pair-ring-picker]';
  const INITIALIZED = 'dgPairInitialized';

  const normalize = (value) => String(value == null ? '' : value).trim().toLowerCase();
  const isMale = (value) => {
    const text = normalize(value);
    return /男裝|男士|男戒|男性/.test(text) || /\bmen\b|\bmen's\b|\bman\b|\bmale\b|\bmens\b/.test(text);
  };
  const isFemale = (value) => {
    const text = normalize(value);
    return /女裝|女士|女戒|女性/.test(text) || /\bwomen\b|\bwoman\b|\bfemale\b|\bwomens\b/.test(text);
  };

  function safeJSON(element, fallback) {
    try {
      return JSON.parse(element?.textContent || '');
    } catch (error) {
      return fallback;
    }
  }

  function moneyWithDelimiters(cents, precision, thousands, decimal) {
    const number = (Number(cents || 0) / 100).toFixed(precision);
    const parts = number.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
  }

  function formatMoney(cents) {
    const settings = window.themeVariables?.settings || {};
    const format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    if (!format) {
      const currency = window.Shopify?.currency?.active || 'HKD';
      return new Intl.NumberFormat(document.documentElement.lang || 'zh-HK', {
        style: 'currency', currency, maximumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    }

    return String(format).replace(/\{\{\s*(\w+)\s*\}\}/, (_match, token) => {
      let precision = 2;
      let thousands = ',';
      let decimal = '.';
      if (token === 'amount_no_decimals' || token === 'amount_no_decimals_with_comma_separator') precision = 0;
      if (token === 'amount_with_comma_separator' || token === 'amount_no_decimals_with_comma_separator') {
        thousands = '.';
        decimal = ',';
      }
      if (token === 'amount_with_apostrophe_separator') thousands = "'";
      return moneyWithDelimiters(cents, precision, thousands, decimal);
    });
  }

  function getRootNodeFor(root) {
    const node = root.getRootNode();
    return node && typeof node.querySelector === 'function' ? node : document;
  }

  function findForm(root, formId) {
    const node = getRootNodeFor(root);
    try {
      return node.querySelector(`#${CSS.escape(formId)}`) || document.getElementById(formId);
    } catch (_error) {
      return document.getElementById(formId);
    }
  }

  function sortSizes(values) {
    return values.sort((a, b) => {
      const left = String(a).match(/\d+(?:\.\d+)?/);
      const right = String(b).match(/\d+(?:\.\d+)?/);
      if (left && right && Number(left[0]) !== Number(right[0])) return Number(left[0]) - Number(right[0]);
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    });
  }

  function init(root) {
    if (root.dataset[INITIALIZED] === 'true') return;

    const options = safeJSON(root.querySelector('[data-dg-pair-options]'), []);
    const variants = safeJSON(root.querySelector('[data-dg-pair-variants]'), []);
    const formId = root.dataset.formId;
    const form = formId ? findForm(root, formId) : null;

    if (!Array.isArray(options) || !Array.isArray(variants) || variants.length === 0 || !form) return;

    const isEnglish = normalize(root.dataset.language).startsWith('en');
    const copy = isEnglish ? {
      maleSize: "Men's ring size",
      femaleSize: "Women's ring size",
      ringSize: 'Ring size',
      pairPrice: "Pair price (men's + women's)",
      malePrice: "Men's ring price",
      femalePrice: "Women's ring price",
      unavailableSize: ' (currently unavailable)',
      unavailableSelection: 'This size or metal combination is currently unavailable. Please select another size.',
      chooseSizes: "Please choose an available men's and women's ring size.",
      addFailed: 'Unable to add the pair to cart. Please try again.',
      purchaseOption: 'Purchase option',
      metal: 'Metal'
    } : {
      maleSize: '男裝戒指尺寸',
      femaleSize: '女裝戒指尺寸',
      ringSize: '戒指尺寸',
      pairPrice: '一對價格（男裝＋女裝）',
      malePrice: '男裝單隻價格',
      femalePrice: '女裝單隻價格',
      unavailableSize: '（暫未供應）',
      unavailableSelection: '此尺寸或金屬組合暫時未能供應，請選擇其他尺寸。',
      chooseSizes: '請先選擇可供應的男裝及女裝戒指尺寸。',
      addFailed: '加入購物車時發生問題，請稍後再試。',
      purchaseOption: '選購方式',
      metal: '金屬'
    };

    const modeSelect = root.querySelector('[data-dg-mode]');
    const metalField = root.querySelector('[data-dg-metal-field]');
    const metalSelect = root.querySelector('[data-dg-metal]');
    const pairFields = root.querySelector('[data-dg-pair-fields]');
    const singleField = root.querySelector('[data-dg-single-field]');
    const singleSizeLabel = root.querySelector('[data-dg-single-size-label]');
    const errorElement = root.querySelector('[data-dg-error]');
    const maleSizeSelect = root.querySelector('[data-dg-size="male"]');
    const femaleSizeSelect = root.querySelector('[data-dg-size="female"]');
    const singleSizeSelect = root.querySelector('[data-dg-size="single"]');
    const variantIdInput = form.querySelector('input[name="id"]');

    if (!modeSelect || !pairFields || !singleField || !maleSizeSelect || !femaleSizeSelect || !singleSizeSelect || !variantIdInput) return;

    const genderIndex = options.findIndex((_name, index) =>
      variants.some((variant) => isMale(variant.options?.[index])) &&
      variants.some((variant) => isFemale(variant.options?.[index]))
    );
    const sizeIndex = options.findIndex((name) => /尺寸|size/i.test(String(name)));
    const metalIndex = options.findIndex((name) => /金屬|metal/i.test(String(name)));

    if (genderIndex < 0 || sizeIndex < 0) {
      root.dataset.dgUnsupported = 'true';
      return;
    }

    const roleMatches = (variant, role) => role === 'male'
      ? isMale(variant.options?.[genderIndex])
      : isFemale(variant.options?.[genderIndex]);
    const selectedMetal = () => metalIndex >= 0 ? metalSelect.value : '';
    const matchesMetal = (variant, metal) => metalIndex < 0 || !metal || variant.options?.[metalIndex] === metal;

    const variantsForRole = (role, metal) => variants.filter((variant) =>
      roleMatches(variant, role) && matchesMetal(variant, metal)
    );

    const findVariant = (role, size, metal) => variants.find((variant) =>
      roleMatches(variant, role) &&
      String(variant.options?.[sizeIndex] ?? '') === String(size ?? '') &&
      matchesMetal(variant, metal)
    );

    const addOption = (select, value, label, disabled = false) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      option.disabled = disabled;
      select.appendChild(option);
    };

    function populateMetals() {
      if (metalIndex < 0) {
        metalField.hidden = true;
        return;
      }
      const metals = [...new Set(variants.map((variant) => variant.options?.[metalIndex]).filter((value) => value != null && value !== ''))];
      metalSelect.replaceChildren();
      metals.forEach((metal) => addOption(metalSelect, metal, metal));
      metalField.hidden = metals.length <= 1;
      if (metals.length === 1) metalSelect.value = metals[0];
    }

    function populateSizes(select, role, preferredSize = '') {
      const roleVariants = variantsForRole(role, selectedMetal());
      const sizes = sortSizes([...new Set(roleVariants.map((variant) => variant.options?.[sizeIndex]).filter((value) => value != null && value !== ''))]);
      select.replaceChildren();
      sizes.forEach((size) => {
        const matching = findVariant(role, size, selectedMetal());
        const unavailable = !matching || matching.available === false;
        addOption(select, size, unavailable ? `${size}${copy.unavailableSize}` : size, unavailable);
      });
      const preferred = sizes.find((size) => String(size) === String(preferredSize) && findVariant(role, size, selectedMetal())?.available !== false);
      const firstAvailable = sizes.find((size) => findVariant(role, size, selectedMetal())?.available !== false);
      select.value = String(preferred ?? firstAvailable ?? sizes[0] ?? '');
    }

    function getPriceScope() {
      return root.closest('.shopify-section') || root.closest('.quick-buy-modal__content') || root.parentElement || document;
    }

    function updatePriceDisplay(totalCents, label) {
      const scope = getPriceScope();
      const formatted = formatMoney(totalCents);
      const selectors = '.price-list--product, .product-sticky-bar .price-list, .quick-buy-modal__mobile-info .price-list';
      const priceLists = scope.querySelectorAll(selectors);
      priceLists.forEach((priceList) => {
        const salePrice = priceList.querySelector('sale-price');
        if (salePrice) {
          const screenReaderText = salePrice.querySelector('.sr-only');
          salePrice.replaceChildren();
          if (screenReaderText) salePrice.appendChild(screenReaderText);
          salePrice.appendChild(document.createTextNode(formatted));
        }
        const compareAtPrice = priceList.querySelector('compare-at-price');
        if (compareAtPrice) compareAtPrice.hidden = true;
      });
      scope.querySelectorAll('[data-dg-live-price]').forEach((element) => { element.textContent = formatted; });
      scope.querySelectorAll('[data-dg-live-price-label]').forEach((element) => { element.textContent = label; });
    }

    function setError(message) {
      if (!errorElement) return;
      errorElement.textContent = message || '';
      errorElement.hidden = !message;
    }

    function getSubmitButtons() {
      const scope = getRootNodeFor(root);
      let selector = `button[form="${formId}"][type="submit"]`;
      try { selector = `button[form="${CSS.escape(formId)}"][type="submit"]`; } catch (_error) { /* keep basic selector */ }
      return [...new Set([
        ...Array.from(form.querySelectorAll('button[type="submit"]')),
        ...Array.from(scope.querySelectorAll(selector))
      ])];
    }

    function updateSubmitAvailability(isAvailable) {
      getSubmitButtons().forEach((button) => {
        button.disabled = !isAvailable;
        if (isAvailable) button.removeAttribute('disabled');
      });
    }

    function selectedVariants() {
      const metal = selectedMetal();
      const mode = modeSelect.value;
      if (mode === 'pair') {
        return {
          mode,
          male: findVariant('male', maleSizeSelect.value, metal),
          female: findVariant('female', femaleSizeSelect.value, metal),
          maleSize: maleSizeSelect.value,
          femaleSize: femaleSizeSelect.value,
          metal
        };
      }
      const role = mode === 'male' ? 'male' : 'female';
      return {
        mode,
        single: findVariant(role, singleSizeSelect.value, metal),
        role,
        size: singleSizeSelect.value,
        metal
      };
    }

    function updateSelection() {
      const selection = selectedVariants();
      const isPair = selection.mode === 'pair';
      const isAvailable = isPair
        ? Boolean(selection.male && selection.female && selection.male.available !== false && selection.female.available !== false)
        : Boolean(selection.single && selection.single.available !== false);
      const selectedVariant = isPair ? selection.male : selection.single;

      if (selectedVariant) {
        variantIdInput.value = selectedVariant.id;
        variantIdInput.disabled = false;
        variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const amount = isPair
        ? ((selection.male?.price || 0) + (selection.female?.price || 0))
        : (selection.single?.price || 0);
      const label = isPair
        ? copy.pairPrice
        : selection.role === 'male' ? copy.malePrice : copy.femalePrice;

      updatePriceDisplay(amount, label);
      updateSubmitAvailability(isAvailable);
      setError(isAvailable ? '' : copy.unavailableSelection);
      root.dataset.dgSelectionMode = selection.mode;
      root.dataset.dgCanAdd = String(isAvailable);
      return { selection, isAvailable };
    }

    function updateMode(preserveSize = true) {
      const mode = modeSelect.value;
      const oldMale = maleSizeSelect.value;
      const oldFemale = femaleSizeSelect.value;
      const oldSingle = singleSizeSelect.value;

      pairFields.hidden = mode !== 'pair';
      singleField.hidden = mode === 'pair';
      if (singleSizeLabel) singleSizeLabel.textContent = mode === 'male' ? copy.maleSize : mode === 'female' ? copy.femaleSize : copy.ringSize;

      if (mode === 'pair') {
        populateSizes(maleSizeSelect, 'male', preserveSize ? oldMale || oldSingle : '');
        populateSizes(femaleSizeSelect, 'female', preserveSize ? oldFemale || oldSingle : '');
      } else {
        populateSizes(singleSizeSelect, mode === 'male' ? 'male' : 'female', preserveSize ? oldSingle || (mode === 'male' ? oldMale : oldFemale) : '');
      }
      updateSelection();
    }

    // Use the first valid metal and default to buying the pair.
    populateMetals();
    modeSelect.value = 'pair';
    updateMode(false);

    modeSelect.addEventListener('change', () => updateMode(true));
    metalSelect.addEventListener('change', () => updateMode(true));
    [maleSizeSelect, femaleSizeSelect, singleSizeSelect].forEach((select) => {
      select.addEventListener('change', updateSelection);
    });

    variantIdInput.disabled = false;
    root.dataset[INITIALIZED] = 'true';

    form.addEventListener('submit', async (event) => {
      if (modeSelect.value !== 'pair') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const { selection, isAvailable } = updateSelection();
      if (!isAvailable) {
        setError(copy.chooseSizes);
        return;
      }

      const buttons = getSubmitButtons();
      buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      const scopeForQuantity = getRootNodeFor(root);
      let quantityInput = form.querySelector('[name="quantity"]');
      if (!quantityInput) {
        try { quantityInput = scopeForQuantity.querySelector(`[name="quantity"][form="${CSS.escape(formId)}"]`); } catch (_error) { /* use default quantity */ }
      }
      const quantity = Math.max(1, parseInt(quantityInput?.value || '1', 10) || 1);
      const sharedProperties = isEnglish ? {
        'Purchase option': 'Pair',
        "Men's ring size": selection.maleSize,
        "Women's ring size": selection.femaleSize
      } : {
        '購買方式': '一對',
        '男裝尺寸': selection.maleSize,
        '女裝尺寸': selection.femaleSize
      };
      if (selection.metal) sharedProperties[copy.metal] = selection.metal;

      const items = [
        {
          id: selection.male.id,
          quantity,
          properties: { ...sharedProperties, [isEnglish ? 'Ring type' : '戒指類型']: isEnglish ? "Men's" : '男裝' }
        },
        {
          id: selection.female.id,
          quantity,
          properties: { ...sharedProperties, [isEnglish ? 'Ring type' : '戒指類型']: isEnglish ? "Women's" : '女裝' }
        }
      ];

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      const payload = { items, sections_url: window.location.pathname };
      if (sectionsToBundle.length) payload.sections = sectionsToBundle;

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });
        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(responseJson.description || responseJson.message || copy.addFailed);
        }

        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = `${window.Shopify.routes.root}cart`;
          return;
        }

        const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })).json();
        cart.sections = responseJson.sections || {};

        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: responseJson.items || [], cart, onSuccessDo: 'force_open_drawer' }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart }
        }));
        setError('');
      } catch (error) {
        setError(error?.message || copy.addFailed);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        buttons.forEach((button) => button.removeAttribute('aria-busy'));
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }, true);
  }

  function initializeWithin(scope) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  } else {
    initializeWithin(document);
  }

  // Also initialize selectors cloned from the theme's quick-buy template.
  const observer = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === 1) initializeWithin(node);
    }));
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
